import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ProgressBar from '@atlassian/jira-common-components-progress-bar/src/main.tsx';
import ProgressElement from '@atlassian/jira-common-components-progress-bar/src/progress-element/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { progressBar_projectVersions_VersionProgress$key } from '@atlassian/jira-relay/src/__generated__/progressBar_projectVersions_VersionProgress.graphql';
import { Container } from '@atlassian/jira-version-modals/src/common/components/text-cell/styled';
import messages from './messages';

export type Props = {
	query: progressBar_projectVersions_VersionProgress$key;
};

const VersionProgress = ({ query }: Props) => {
	const { formatMessage } = useIntl();

	const progress = useFragment(
		graphql`
			fragment progressBar_projectVersions_VersionProgress on JiraVersion {
				done: issues(filter: DONE) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				inProgress: issues(filter: IN_PROGRESS) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
				toDo: issues(filter: TODO) @required(action: THROW) {
					totalCount @required(action: THROW)
				}
			}
		`,
		query,
	);

	const doneCount = progress.done.totalCount;
	const inProgressCount = progress.inProgress.totalCount;
	const toDoCount = progress.toDo.totalCount;

	return toDoCount || inProgressCount || doneCount ? (
		<BarContainer>
			<ProgressBar
				sum={doneCount + inProgressCount + toDoCount}
				remainingTooltipContent={formatMessage(messages.toDo, {
					toDo: toDoCount,
				})}
				backgroundColor={token('color.border', colors.N30)}
			>
				<ProgressElement
					value={doneCount}
					tooltipContent={formatMessage(messages.done, {
						done: doneCount,
					})}
					color={token('color.border.success', colors.G300)}
				/>
				<ProgressElement
					value={inProgressCount}
					tooltipContent={formatMessage(messages.inProgress, {
						inProgress: inProgressCount,
					})}
					color={token('color.border.information', colors.B200)}
				/>
			</ProgressBar>
		</BarContainer>
	) : (
		<TextContainer>{formatMessage(messages.empty)}</TextContainer>
	);
};

const TextContainer = Container;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BarContainer = styled(Container)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingTop: `${gridSize * 1.65}px`,
});

export default VersionProgress;
